import React, { useEffect, useMemo, useState } from "react";
import { useResetPassword } from "../../hooks/auth";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Fade,
  TextField,
  Typography,
  Alert,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  CircularProgress,
  useTheme} from "@mui/material";
import { Lock as LockIcon, Visibility, VisibilityOff, CheckCircleOutline, LockReset } from "@mui/icons-material";
import PasswordStrengthMeter from "../../components/common/PasswordStrengthMeter";
import { isPasswordCompliant } from "../../utils/passwordPolicy";
import { ApiError } from "../../error/errorTypes";
import { LOGIN_ROUTE_PATH } from "../../utils/constants/AppConstants";

// strength handled by shared util + component

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);
  const theme = useTheme();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState<number>(3);

  const { mutate: resetPassword, isPending: isSubmitting } = useResetPassword();

  const canSubmit = token && isPasswordCompliant(password) && password === confirmPassword && !isSubmitting;

  useEffect(() => {
    if (!success) return;
    const timer = setInterval(() => {
      setRedirectCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate(LOGIN_ROUTE_PATH);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [success, navigate]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    resetPassword(
      { token, newPassword: password },
      {
        onSuccess: () => {
          setSuccess(true);
        },
        onError: (err) => {
          if (err instanceof ApiError) {
            setError(err.userMessage || err.message);
          } else {
            setError("Password reset failed. The link may be invalid or expired.");
          }
        }
      }
    );
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
        display: "flex",
        alignItems: "center",
        py: 4}}
    >
      <Container maxWidth="sm">
        <Fade in={true} timeout={700}>
          <Card
            sx={{
              background: theme.palette.background.paper,
              backdropFilter: "blur(10px)",
              border: `1px solid ${theme.palette.divider}`}}
          >
            <CardContent sx={{ p: 5 }}>
              <Typography
                variant="h4"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent"}}
              >
                Reset your password
              </Typography>

              {!token && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} role="alert">
                  Missing reset token. Please use the link from your email.
                </Alert>
              )}
              {error && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} role="alert">
                  {error}
                </Alert>
              )}

              {success ? (
                <Box sx={{ textAlign: "center" }}>
                  <CheckCircleOutline sx={{ color: theme.palette.success.main, fontSize: 48, mb: 2 }} />
                  <Typography variant="h5" gutterBottom>Password updated</Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    Your password has been changed. You can now sign in.
                  </Typography>
                  <Button variant="contained" onClick={() => navigate(LOGIN_ROUTE_PATH)}>
                    Go to sign in {redirectCountdown > 0 ? `(${redirectCountdown}s)` : ""}
                  </Button>
                </Box>
              ) : (
                <Box component="form" onSubmit={handleSubmit}>
                  <TextField
                    fullWidth
                    id="new-password"
                    label="New password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={!token || isSubmitting}
                    sx={{ mb: 2 }}
                    helperText="Use at least 8 characters, with uppercase, lowercase, number, and symbol."
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword((v) => !v)} onMouseDown={(e) => e.preventDefault()} edge="end">
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    error={password.length > 0 && !isPasswordCompliant(password)}
                  />

                  <PasswordStrengthMeter password={password} />

                  <TextField
                    fullWidth
                    id="confirm-password"
                    label="Confirm new password"
                    type={showConfirm ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={!token || isSubmitting}
                    sx={{ mb: 3 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowConfirm((v) => !v)} onMouseDown={(e) => e.preventDefault()} edge="end">
                            {showConfirm ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    error={confirmPassword.length > 0 && confirmPassword !== password}
                    helperText={confirmPassword && confirmPassword !== password ? "Passwords don't match." : ""}
                  />

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={!canSubmit}
                    sx={{
                      py: 1.5,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                      '&:hover': {
                        background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`}}}
                    startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <LockReset />}
                    aria-busy={isSubmitting ? 'true' : undefined}
                  >
                    {isSubmitting ? 'Updating...' : 'Change password'}
                  </Button>

                  {!token && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      Don’t have a valid link? <MuiLink href="#" onClick={(e) => { e.preventDefault(); navigate('/forgot-password'); }}>Request a new reset link</MuiLink>.
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Fade>
      </Container>
    </Box>
  );
};

export default ResetPasswordPage;


